import React, { useState, useEffect } from 'react';
import { COMPANY_INFO, SERVICES_DATA } from '../data/companyData';
import { LeadFormData } from '../types';
import { submitContactToSupabase } from '../lib/supabase';
import { Phone, Mail, MapPin, MessageSquare, Send, CheckCircle2, Sparkles, Clock, Globe, ShieldCheck, Loader2, Linkedin, Copy, Check, Database, Trash2, ExternalLink, FileText } from 'lucide-react';

interface ContactSectionProps {
  initialServiceRequirement?: string;
  onFormSubmitted?: (formData: LeadFormData) => void;
}

interface StoredLead extends LeadFormData {
  id: string;
  timestamp: string;
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  initialServiceRequirement,
  onFormSubmitted
}) => {
  const [formData, setFormData] = useState<LeadFormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    serviceCategory: initialServiceRequirement || 'Adobe Creative Cloud Services',
    projectScope: '100-500 Pages',
    message: '',
    preferredContact: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [savedLeads, setSavedLeads] = useState<StoredLead[]>([]);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);

  useEffect(() => {
    if (initialServiceRequirement) {
      setFormData((prev) => ({
        ...prev,
        serviceCategory: initialServiceRequirement
      }));
    }
  }, [initialServiceRequirement]);

  // Load stored leads from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('asap_learning_leads');
      if (stored) {
        setSavedLeads(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Failed to load stored leads:', err);
    }
  }, []);

  const generateMailBodyText = (data: LeadFormData) => {
    return (
      `ASAP LEARNING NEW PROJECT LEAD\n` +
      `==================================\n` +
      `• Destination Email: ${COMPANY_INFO.email}\n` +
      `• Date Submitted: ${new Date().toLocaleString()}\n\n` +
      `CLIENT CONTACT DETAILS:\n` +
      `• Full Name: ${data.fullName}\n` +
      `• Work Email: ${data.email}\n` +
      `• Phone Number: ${data.phone}\n` +
      `• Organization: ${data.company || 'N/A'}\n\n` +
      `PROJECT REQUIREMENTS:\n` +
      `• Service Category: ${data.serviceCategory}\n` +
      `• Project Volume / Scope: ${data.projectScope}\n` +
      `• Preferred Response: ${data.preferredContact}\n\n` +
      `SPECIAL INSTRUCTIONS / NOTES:\n` +
      `${data.message || 'No additional notes provided.'}\n` +
      `==================================`
    );
  };

  const generateMailtoUrl = () => {
    const subject = encodeURIComponent(`[Lead] ASAP LEARNING Inquiry: ${formData.serviceCategory} - ${formData.fullName}`);
    const body = encodeURIComponent(generateMailBodyText(formData));
    return `mailto:${COMPANY_INFO.email}?subject=${subject}&body=${body}`;
  };

  const generateGmailComposeUrl = () => {
    const subject = encodeURIComponent(`[Lead] ASAP LEARNING Inquiry: ${formData.serviceCategory} - ${formData.fullName}`);
    const body = encodeURIComponent(generateMailBodyText(formData));
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${COMPANY_INFO.email}&su=${subject}&body=${body}`;
  };

  const generateWhatsAppUrl = () => {
    const text = encodeURIComponent(
      `Hi ASAP LEARNING Team, I submitted a project inquiry for ${formData.serviceCategory}.\n\n` +
      `Name: ${formData.fullName}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nScope: ${formData.projectScope}`
    );
    return `https://wa.me/${COMPANY_INFO.whatsappNumber}?text=${text}`;
  };

  const handleCopyPayload = () => {
    const text = generateMailBodyText(formData);
    navigator.clipboard.writeText(text);
    setCopiedPayload(true);
    setTimeout(() => setCopiedPayload(false), 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.fullName.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim() ||
      !formData.company.trim() ||
      !formData.message.trim()
    ) {
      alert('All fields are mandatory. Please fill in all required details before submitting.');
      return;
    }

    setIsSubmitting(true);

    const safeMessage = formData.message.trim();

    // Store lead into localStorage so lead is permanently saved on device
    const newLead: StoredLead = {
      ...formData,
      message: safeMessage,
      id: Date.now().toString(),
      timestamp: new Date().toLocaleString()
    };

    try {
      const existing = JSON.parse(localStorage.getItem('asap_learning_leads') || '[]');
      const updated = [newLead, ...existing];
      localStorage.setItem('asap_learning_leads', JSON.stringify(updated));
      setSavedLeads(updated);
    } catch (err) {
      console.error('Error saving lead locally:', err);
    }

    // Direct HTTP POST to deliver lead directly to secure server route and inbox
    try {
      submitContactToSupabase({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.company,
        serviceRequirement: formData.serviceCategory,
        estimatedBudget: formData.projectScope,
        message: safeMessage
      }).catch(err => console.log('Client Supabase submission note:', err));

      await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          organization: formData.company || 'N/A',
          serviceRequirement: formData.serviceCategory,
          estimatedBudget: formData.projectScope,
          message: safeMessage
        })
      });

      // Also submit to email dispatch endpoint as backup
      await fetch(`https://formsubmit.co/ajax/${COMPANY_INFO.email}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `New ASAP LEARNING Lead: ${formData.fullName} (${formData.serviceCategory})`,
          client_name: formData.fullName,
          client_email: formData.email,
          client_phone: formData.phone,
          organization: formData.company || 'N/A',
          service_requested: formData.serviceCategory,
          project_scope: formData.projectScope,
          preferred_contact: formData.preferredContact,
          project_details: safeMessage,
          _template: 'table'
        })
      });
    } catch (err) {
      console.log('HTTP lead transmission note:', err);
    }

    setIsSubmitting(false);
    setIsSuccess(true);

    if (onFormSubmitted) {
      onFormSubmitted({ ...formData, message: safeMessage });
    }
  };

  const handleClearLeads = () => {
    if (confirm('Are you sure you want to clear all recorded leads from local storage?')) {
      localStorage.removeItem('asap_learning_leads');
      setSavedLeads([]);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 bg-slate-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Call To Action Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 border border-blue-200 text-blue-800 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" /> Start Your Project
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Let's Build Your Next Publishing Project
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            Connect with our publishing specialists for a free scope analysis, sample conversion, or formal proposal.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          
          {/* Left Column: Direct Contact Info & Embedded Map Card */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200/90 shadow-sm flex flex-col justify-between space-y-5 h-full">
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">Direct Contact Information</h3>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Reach out directly or send us your sample chapter to test our turnaround speeds and formatting quality.
                </p>
              </div>

              {/* 2-Column Contact Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {/* Phone */}
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-blue-50 hover:border-blue-200 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Phone / Hotline</p>
                    <p className="text-xs font-bold text-slate-900 truncate group-hover:text-blue-700">{COMPANY_INFO.phone}</p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-blue-50 hover:border-blue-200 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Official Email</p>
                    <p className="text-xs font-bold text-slate-900 truncate group-hover:text-purple-700">{COMPANY_INFO.email}</p>
                  </div>
                </a>

                {/* WhatsApp */}
                <a
                  href={`https://wa.me/${COMPANY_INFO.whatsappNumber}?text=Hi%20ASAP%20Learning%2C%20I%20would%20like%20to%20discuss%20a%20publishing%20project.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-2xl bg-emerald-50 border border-emerald-100 hover:bg-emerald-100/70 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-emerald-800 font-semibold uppercase tracking-wider">WhatsApp Chat</p>
                    <p className="text-xs font-bold text-emerald-950 truncate">+91 7817899559</p>
                  </div>
                </a>

                {/* LinkedIn Company Profile */}
                <a
                  href={COMPANY_INFO.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-2xl bg-blue-50/70 border border-blue-100 hover:bg-blue-100/80 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#0A66C2] text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform shadow-md shadow-blue-600/20">
                    <Linkedin className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-blue-800 font-semibold uppercase tracking-wider">LinkedIn</p>
                    <p className="text-xs font-bold text-slate-900 truncate group-hover:text-[#0A66C2]">asap-learning-global</p>
                  </div>
                </a>
              </div>

              {/* Address HQ */}
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Headquarters</p>
                  <p className="text-xs font-bold text-slate-900">{COMPANY_INFO.address}</p>
                </div>
              </div>
            </div>

            {/* Embedded Map Header & Expanded Vertical Frame */}
            <div className="pt-2 flex-1 flex flex-col min-h-[200px]">
              <div className="flex items-center justify-between mb-2 shrink-0">
                <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-blue-600" /> Dehradun Production Center
                </span>
                <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-300/80 px-2.5 py-0.5 rounded-full shadow-2xs">
                  24/7 Operations Hub
                </span>
              </div>
              <div className="w-full min-h-[200px] h-full flex-1 rounded-2xl overflow-hidden border border-slate-200 shadow-inner">
                <iframe
                  title="ASAP LEARNING Location"
                  src={COMPANY_INFO.mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '200px' }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

          </div>

          {/* Right Column: Lead Capture Form */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200/90 shadow-sm relative overflow-hidden flex flex-col justify-between">
            
            {/* Form Title & Clean Header */}
            <div className="mb-5 pb-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">Request a Proposal or Sample</h3>
                <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                  Fill in your project specifications below. Our team will contact you shortly.
                </p>
              </div>
            </div>

              {isSuccess ? (
                <div className="p-6 sm:p-8 rounded-2xl bg-emerald-50/90 border border-emerald-200 text-center space-y-5 animate-in fade-in duration-300">
                  <div className="w-16 h-16 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto shadow-lg shadow-emerald-600/30">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-extrabold text-emerald-950">Inquiry Captured & Dispatched!</h4>
                    <p className="text-xs sm:text-sm text-emerald-800 max-w-md mx-auto mt-2 leading-relaxed">
                      Thank you, <span className="font-bold">{formData.fullName}</span>. Your <span className="font-bold">{formData.serviceCategory}</span> lead details have been logged in local memory and formatted for <span className="underline font-bold text-emerald-950">{COMPANY_INFO.email}</span>.
                    </p>
                  </div>

                  {/* Multi-Channel Dispatch Box */}
                  <div className="p-4 rounded-2xl bg-white border border-emerald-200 shadow-sm text-left max-w-lg mx-auto space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700 pb-2 border-b border-slate-100">
                      <span className="flex items-center gap-1.5 text-purple-700 font-semibold">
                        <Mail className="w-4 h-4 text-purple-600" /> Target Email Address:
                      </span>
                      <span className="text-slate-900 font-mono bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-bold">
                        {COMPANY_INFO.email}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                      {/* Option 1: Mailto App */}
                      <a
                        href={generateMailtoUrl()}
                        className="py-3 px-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm"
                      >
                        <Mail className="w-4 h-4 shrink-0" /> Open Mail App
                      </a>

                      {/* Option 2: Gmail Web Compose */}
                      <a
                        href={generateGmailComposeUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-3 px-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm"
                      >
                        <ExternalLink className="w-4 h-4 shrink-0" /> Open in Gmail
                      </a>

                      {/* Option 3: Copy Text Payload */}
                      <button
                        type="button"
                        onClick={handleCopyPayload}
                        className={`py-3 px-3.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm border ${
                          copiedPayload
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-slate-900 hover:bg-slate-800 text-white border-slate-900'
                        }`}
                      >
                        {copiedPayload ? (
                          <>
                            <Check className="w-4 h-4 text-white" /> Payload Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> Copy Email Payload
                          </>
                        )}
                      </button>

                      {/* Option 4: WhatsApp Alert */}
                      <a
                        href={generateWhatsAppUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-3 px-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm"
                      >
                        <MessageSquare className="w-4 h-4 shrink-0" /> Send via WhatsApp
                      </a>
                    </div>
                  </div>

                  <div className="pt-2 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setIsSuccess(false)}
                      className="px-6 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition-colors"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Full Name */}
                    <div>
                      <label htmlFor="contact-full-name" className="block text-xs font-bold text-slate-800 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-full-name"
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="e.g. Sarah Jenkins"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none transition-all"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-bold text-slate-800 mb-1">
                        Work Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="e.g. s.jenkins@publisher.com"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Phone */}
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-bold text-slate-800 mb-1">
                        Phone / Mobile <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none transition-all"
                      />
                    </div>

                    {/* Company / Institution */}
                    <div>
                      <label htmlFor="contact-company" className="block text-xs font-bold text-slate-800 mb-1">
                        Organization / Publisher Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        required
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="e.g. Cambridge Press / Univ."
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Service Dropdown */}
                    <div>
                      <label htmlFor="primary-service-select" className="block text-xs font-bold text-slate-800 mb-1">
                        Primary Service Required <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="primary-service-select"
                        required
                        value={formData.serviceCategory}
                        onChange={(e) => setFormData({ ...formData, serviceCategory: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none bg-white transition-all"
                      >
                        {SERVICES_DATA.map((s) => (
                          <option key={s.id} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                        <option value="Full Publishing Production Package">Full Publishing Production Package</option>
                      </select>
                    </div>

                    {/* Project Scope / Volume */}
                    <div>
                      <label htmlFor="project-scope-select" className="block text-xs font-bold text-slate-800 mb-1">
                        Project Volume / Pages <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="project-scope-select"
                        required
                        value={formData.projectScope}
                        onChange={(e) => setFormData({ ...formData, projectScope: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none bg-white transition-all"
                      >
                        <option value="Sample Chapter (< 50 pgs)">Sample Chapter (&lt; 50 pgs)</option>
                        <option value="100-500 Pages">100 - 500 Pages (Single Title)</option>
                        <option value="500-2,000 Pages">500 - 2,000 Pages (Series / Journal)</option>
                        <option value="2,000+ Pages Batch">2,000+ Pages (Large Enterprise Batch)</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-bold text-slate-800 mb-1">
                      Project Details / Special Requirements <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      rows={3}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Specify layout guidelines, DTD rules, WCAG target level, or deadline constraints..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 text-sm text-slate-900 outline-none transition-all"
                    />
                  </div>

                  {/* Preferred Contact Method */}
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-2">Preferred Response Mode</label>
                    <div className="flex gap-4">
                      {[
                        { id: 'email', label: 'Email Proposal' },
                        { id: 'phone', label: 'Phone Call' },
                        { id: 'whatsapp', label: 'WhatsApp Chat' }
                      ].map((m) => (
                        <label key={m.id} className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                          <input
                            type="radio"
                            name="preferredContact"
                            checked={formData.preferredContact === m.id}
                            onChange={() => setFormData({ ...formData, preferredContact: m.id as any })}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2 space-y-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 rounded-xl text-white font-extrabold text-sm shadow-xl transition-all duration-300 flex items-center justify-center gap-2.5 relative overflow-hidden ${
                        isSubmitting
                          ? 'bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 cursor-wait opacity-90 shadow-blue-500/10'
                          : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-blue-500/25 hover:shadow-2xl hover:scale-[1.005]'
                      }`}
                    >
                      {/* Active Loading Progress Bar Overlay */}
                      {isSubmitting && (
                        <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 animate-pulse w-full" />
                      )}

                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-white shrink-0" />
                          <span className="tracking-wide">Sending Lead Data to Server...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Submit Proposal Request</span>
                        </>
                      )}
                    </button>

                    {isSubmitting && (
                      <p className="text-center text-[11px] font-semibold text-blue-600 animate-pulse flex items-center justify-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-600 animate-ping inline-block" />
                        Transmitting project details...
                      </p>
                    )}
                  </div>

                  <p className="text-center text-[11px] text-slate-500">
                    🔒 Strict Confidentiality: Non-Disclosure Agreements (NDAs) standard on all publisher manuscripts.
                  </p>
                </form>
              )}

            </div>
          </div>

        </div>

      {/* Recorded Leads Drawer / Viewer Modal */}
      {showLeadsModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border border-slate-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <Database className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-lg font-extrabold text-slate-900">Recorded Leads Database</h4>
                  <p className="text-xs text-slate-500">Destination: <span className="font-mono font-bold text-purple-700">{COMPANY_INFO.email}</span></p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowLeadsModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-sm transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {savedLeads.length === 0 ? (
                <div className="text-center py-12 space-y-3">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto" />
                  <p className="text-sm font-semibold text-slate-600">No leads captured yet.</p>
                  <p className="text-xs text-slate-400 max-w-xs mx-auto">Submit a test inquiry using the form to see lead records stored here.</p>
                </div>
              ) : (
                savedLeads.map((lead, idx) => (
                  <div key={lead.id || idx} className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
                    <div className="flex items-center justify-between text-xs font-bold text-slate-900">
                      <span className="text-blue-700 font-extrabold">{lead.fullName} ({lead.company || 'Private Client'})</span>
                      <span className="text-slate-400 text-[11px] font-mono">{lead.timestamp}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                      <div><span className="font-semibold text-slate-800">Email:</span> {lead.email}</div>
                      <div><span className="font-semibold text-slate-800">Phone:</span> {lead.phone}</div>
                      <div><span className="font-semibold text-slate-800">Service:</span> {lead.serviceCategory}</div>
                      <div><span className="font-semibold text-slate-800">Scope:</span> {lead.projectScope}</div>
                    </div>

                    {lead.message && (
                      <div className="p-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 font-mono">
                        "{lead.message}"
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <span className="text-xs font-bold text-slate-500">
                Total Leads Captured: {savedLeads.length}
              </span>

              <div className="flex items-center gap-2">
                {savedLeads.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearLeads}
                    className="px-3.5 py-2 rounded-xl bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 font-bold text-xs flex items-center gap-1.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Clear All
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setShowLeadsModal(false)}
                  className="px-5 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition-colors"
                >
                  Close Window
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </section>
  );
};
